// Payment-method options for the memberships page filter (matched against contract.payment_type).
export const PAYMENT_METHODS = ['Credit Card', 'Cash', 'Check', 'Billing'] as const;

// Legacy contract rows may carry lowercase / abbreviated payment_type strings.
// Normalize to the canonical PAYMENT_METHODS form for filtering + display.
export function normalizePaymentMethod(raw: string | undefined | null): string {
  if (!raw) return '';
  const key = raw.trim().toLowerCase();
  if (key === 'card' || key === 'cc' || key === 'credit card') return 'Credit Card';
  if (key === 'cash') return 'Cash';
  if (key === 'check' || key === 'cheque') return 'Check';
  return raw;
}

// PM (preventive maintenance) job type — matches backend PMJobTypeID.
export const PM_JOB_TYPE_ID = 3;

// contract_billing_type ids. Cancelling autobill sets MANUAL but keeps helcim_subscription_id on the
// row, so autobill-on needs both fields.
export const BILLING_TYPE_HELCIM_AUTO = 1;
export const BILLING_TYPE_MANUAL = 2;

/** membership_status seed id (migration 320): the born state Create stamps; status_id carries an
 * FK to the lookup, so "no stamp" is this row, not 0. */
export const MEMBERSHIP_STATUS_AWAITING_CONFIRMATION = 2;

// Half a cent of float slack when comparing money, mirroring core's amountEpsilon.
export const AMOUNT_TOLERANCE = 0.005;

/**
 * Whether Helcim is actually billing this contract. Both halves matter: the subscription id stays
 * on the row after a cancellation for audit, and a contract can carry the auto-renew billing type
 * while its subscription failed to start — neither is autobilling.
 */
export const isAutobilling = (contract: {
  helcimSubscriptionId: number;
  billingTypeId: number;
}): boolean => contract.helcimSubscriptionId > 0 && contract.billingTypeId !== BILLING_TYPE_MANUAL;

// Renewal reminder triggers, grouped by cadence track and ordered earliest-to-latest
// before expiration. Keys must match the backend membershiprenewal trigger keys.
// Track selection is server-side: helcim_auto contracts auto-renew, and the rest expire on
// the commercial cadence when the plan is Custom or the residential one otherwise. The
// server refuses a test reminder off the contract's own track.
// The expiration track's touchpoints, with how many days before the renewal date each one fires.
// Named separately because the Renewals Due window is derived from the earliest of them.
const EXPIRATION_TRIGGERS = [
  { key: 'email_7wk', label: '7-week email', daysBefore: 49 },
  { key: 'text_6wk', label: '6-week text', daysBefore: 42 },
  { key: 'email_4wk', label: '4-week email', daysBefore: 28 },
  { key: 'text_3wk', label: '3-week text', daysBefore: 21 },
  { key: 'text_1wk', label: '1-week text', daysBefore: 7 },
  { key: 'call_5d', label: '5-day call', daysBefore: 5 },
] as const;

export const RENEWAL_TRIGGER_GROUPS = [
  {
    label: 'Auto-renew track',
    triggers: [
      { key: 'ar_email_4wk', label: '4-week email' },
      { key: 'ar_text_4wk', label: '4-week text' },
      { key: 'ar_email_2wk', label: '2-week email' },
      { key: 'ar_text_2wk', label: '2-week text' },
      { key: 'ar_email_1wk', label: '1-week email' },
      { key: 'ar_text_1wk', label: '1-week text' },
    ],
  },
  { label: 'Expiration track', triggers: EXPIRATION_TRIGGERS },
  {
    // Custom-plan (commercial) contracts follow this instead of the expiration track. Two
    // emails and no texts: every expiration text quotes a retail price or names a consumer
    // plan, neither of which applies to a negotiated commercial rate.
    label: 'Commercial (Custom plan) track',
    triggers: [
      { key: 'comm_email_6wk', label: '6-week email' },
      { key: 'comm_email_2wk', label: '2-week email' },
    ],
  },
  {
    label: 'Pending payment',
    triggers: [
      { key: 'pp_email_7d', label: '7-day email' },
      { key: 'pp_text_7d', label: '7-day text' },
    ],
  },
] as const;

/**
 * How far ahead of its renewal date a contract counts as "coming up for renewal": the lead time of
 * the earliest expiration reminder, so a CSR working the Renewals Due list is seeing exactly the
 * contracts whose customers have started hearing from us. Derived rather than restated, so moving
 * the first touchpoint moves the list with it.
 *
 * Deliberately the expiration track alone: the commercial track's earliest touchpoint is 42
 * days, inside this window already, so widening the derivation would change nothing today and
 * silently move the list if the commercial cadence ever starts earlier.
 */
export const RENEWAL_WINDOW_DAYS = Math.max(...EXPIRATION_TRIGGERS.map((t) => t.daysBefore));

export const RENEWAL_TRIGGERS = [
  ...RENEWAL_TRIGGER_GROUPS[0].triggers,
  ...RENEWAL_TRIGGER_GROUPS[1].triggers,
  ...RENEWAL_TRIGGER_GROUPS[2].triggers,
  ...RENEWAL_TRIGGER_GROUPS[3].triggers,
] as const;

export type RenewalTriggerKey = (typeof RENEWAL_TRIGGERS)[number]['key'];

export const RENEWAL_TRIGGER_LABELS: Record<string, string> = Object.fromEntries(
  RENEWAL_TRIGGERS.map(({ key, label }) => [key, label]),
);
